import {
  menuCategories,
  operatorCategories,
  SUPERADMIN_VISIBLE_CATEGORIES,
} from '../config/navigation';
import { PrefetchLink } from './PrefetchLink';
import { SyncStatusIndicator } from './SyncStatusIndicator';
import { Outlet, Link, useLocation } from 'react-router';
import { LogOut, ChevronDown, ChevronRight, HelpCircle, X, Check, AlertTriangle, Keyboard, Zap, Mic, FileText, BarChart3, Trophy, Bell } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { NotificationBell } from './NotificationBell';
import { Onboarding } from './Onboarding';
import { startTutorial } from './Onboarding';
import { useState, useEffect } from 'react';
import logoImg from '../../assets/a50295a0af56ffb8dcd053f3d73ca9d37c948ba6.png';
import logoDarkImg from '../../assets/ff6cec488a700597f19b04a772a6a682dadfe9e6.png';

// Theme initialization and management
const initializeTheme = () => {
  const savedTheme = localStorage.getItem('simuladesk-theme') || 'light';
  applyTheme(savedTheme);
  return savedTheme;
};

const applyTheme = (theme: string) => {
  if (theme === 'auto') {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    document.documentElement.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
  } else {
    document.documentElement.setAttribute('data-theme', theme);
  }
};

export function DashboardLayout() {
  const location = useLocation();
  const { user, logout, isAuthLoading, canViewAllOperators, canEditScorecard, canUploadAudits } = useAuth();
  const [expandedCategories, setExpandedCategories] = useState<string[]>(['SUPER ADMIN', 'PRINCIPAL', 'MEU ESPAÇO', 'MEU ESPACO', 'MONITORAMENTO', 'TREINAMENTO', 'MINHAS AVALIAÇÕES', 'MINHAS AVALIACOES', 'DESEMPENHO', 'GESTÃO', 'GESTAO', 'QUALIDADE', 'ADMIN', 'CONTA']);
  const [currentTheme, setCurrentTheme] = useState(initializeTheme);
  
  // Modal States
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showKeyboardModal, setShowKeyboardModal] = useState(false);
  const [showQuickActionsModal, setShowQuickActionsModal] = useState(false);
  
  // Toast State
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  // Show toast notification
  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Listen for system theme changes when in auto mode
  useEffect(() => {
    if (currentTheme !== 'auto') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => applyTheme('auto');
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [currentTheme]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl/Cmd + K - Quick Actions
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setShowQuickActionsModal(true);
      }
      // Ctrl/Cmd + / - Keyboard Shortcuts
      if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault();
        setShowKeyboardModal(true);
      }
      // Ctrl/Cmd + H - Help
      if ((e.ctrlKey || e.metaKey) && e.key === 'h') {
        e.preventDefault();
        setShowHelpModal(true);
      }
      // ESC - Close modals
      if (e.key === 'Escape') {
        setShowLogoutModal(false);
        setShowHelpModal(false);
        setShowKeyboardModal(false);
        setShowQuickActionsModal(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleThemeChange = (theme: string) => {
    setCurrentTheme(theme);
    applyTheme(theme);
    localStorage.setItem('simuladesk-theme', theme);
    showToast(`Tema alterado para ${theme}`, 'success');
  };

  const handleLogout = async () => {
    await logout();
    showToast('Logout realizado com sucesso', 'success');
    setShowLogoutModal(false);
  };

  if (isAuthLoading) {
    return null;
  }

  if (!user) {
    return null;
  }

  // Usar categorias específicas do operador quando logado como operador
  const displayCategories = user?.role === 'operador' ? operatorCategories : menuCategories;

  const toggleCategory = (label: string) => {
    setExpandedCategories(prev =>
      prev.includes(label)
        ? prev.filter(cat => cat !== label)
        : [...prev, label]
    );
  };

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    if (href === '/qualidade') {
      return location.pathname === '/qualidade';
    }
    return location.pathname.startsWith(href);
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'superadmin':
        return 'Super Admin';
      case 'admin':
        return 'Administrador';
      case 'qualidade':
        return 'Qualidade';
      case 'supervisor':
        return 'Supervisor';
      case 'operador':
        return 'Operador';
      default:
        return role;
    }
  };

  return (
    <div className="flex h-screen" style={{ fontFamily: 'var(--font-family-inter)' }}>
      {/* Sidebar */}
      <aside
        className="w-64 border-r flex flex-col"
        style={{
          backgroundColor: 'var(--sidebar)',
          borderColor: 'var(--sidebar-border)',
        }}
      >
        <div className="p-6 border-b" style={{ borderColor: 'var(--sidebar-border)' }}>
          <img 
            src={currentTheme === 'dark' ? logoDarkImg : logoImg}
            alt="SimulaDesk Logo"
            style={{ 
              width: '100%',
              height: 'auto',
              maxHeight: '60px',
              objectFit: 'contain',
            }}
          />
        </div>

        <nav 
          className="flex-1 p-3 overflow-y-auto"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--muted-foreground) var(--sidebar)',
          }}
        >
          {/* Para superadmin, mostrar apenas grupos relevantes à gestão macro */}
          {(() => {
            const categoriesToRender = user?.role === 'superadmin'
              ? displayCategories.filter(cat => SUPERADMIN_VISIBLE_CATEGORIES.has(cat.label))
              : displayCategories;

            return categoriesToRender.map((category) => {
              const visibleItems = category.items.filter(item => item.roles.includes(user?.role || ''));
              if (visibleItems.length === 0) return null;

              const isExpanded = expandedCategories.includes(category.label);

              return (
                <div key={category.label} style={{ marginBottom: '16px' }}>
                  {/* Category Header */}
                  <button
                    onClick={() => toggleCategory(category.label)}
                    className="flex items-center justify-between w-full px-3 py-2 transition-opacity hover:opacity-80"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-weight-semibold)',
                        color: 'var(--muted-foreground)',
                        letterSpacing: '0.5px',
                      }}
                    >
                      {category.label}
                    </span>
                    {isExpanded ? (
                      <ChevronDown size={14} style={{ color: 'var(--muted-foreground)' }} />
                    ) : (
                      <ChevronRight size={14} style={{ color: 'var(--muted-foreground)' }} />
                    )}
                  </button>

                  {/* Category Items */}
                  {isExpanded && (
                    <div style={{ marginTop: '4px' }}>
                      {visibleItems.map((item) => {
                        const Icon = item.icon;
                        const active = isActive(item.href);
                        return (
                          <PrefetchLink
                            key={item.name}
                            to={item.href}
                            className="flex items-center gap-3 px-3 py-2.5 transition-all hover:bg-opacity-50"
                            style={{
                              borderRadius: 'var(--radius-button)',
                              backgroundColor: active ? 'color-mix(in srgb, var(--sidebar-primary) 15%, transparent)' : 'transparent',
                              color: active ? 'var(--sidebar-accent-foreground)' : 'var(--sidebar-foreground)',
                              fontSize: 'var(--text-sm)',
                              fontWeight: active ? 'var(--font-weight-medium)' : 'var(--font-weight-normal)',
                              textDecoration: 'none',
                              marginBottom: '2px',
                            }}
                          >
                            <div
                              className="flex items-center justify-center"
                              style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '8px',
                                backgroundColor: 'color-mix(in srgb, currentColor 10%, transparent)',
                                flexShrink: 0,
                                color: item.color,
                              }}
                            >
                              <Icon size={16} />
                            </div>
                            <span style={{ flex: 1 }}>{item.name}</span>
                          </PrefetchLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            });
          })()}
        </nav>

        {/* Tutorial Button - Sidebar Bottom */}
        <div
          className="p-3 border-t"
          style={{ borderColor: 'var(--sidebar-border)' }}
        >
          {/* Sync Status */}
          <div style={{ marginBottom: '8px' }}>
            <SyncStatusIndicator />
          </div>
          <button
            onClick={startTutorial}
            className="w-full flex items-center gap-3 px-3 py-2.5 transition-all hover:opacity-80"
            style={{
              backgroundColor: 'color-mix(in srgb, var(--secondary) 12%, transparent)',
              color: 'var(--sidebar-foreground)',
              borderRadius: 'var(--radius-button)',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'var(--font-family-inter)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)',
              textAlign: 'left',
            }}
            title="Rever o tutorial interativo da plataforma"
          >
            <div
              className="flex items-center justify-center"
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                backgroundColor: 'color-mix(in srgb, var(--secondary) 20%, transparent)',
                color: 'var(--secondary)',
                flexShrink: 0,
              }}
            >
              <HelpCircle size={16} />
            </div>
            <div>
              <span style={{ display: 'block', lineHeight: '1.3' }}>Rever Tutorial</span>
              <span
                style={{
                  display: 'block',
                  fontSize: 'var(--text-xs)',
                  color: 'var(--muted-foreground)',
                  lineHeight: '1.3',
                  fontWeight: 'var(--font-weight-normal)',
                }}
              >
                Tour guiado pela plataforma
              </span>
            </div>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto" style={{ backgroundColor: 'var(--background)' }}>
        {/* Top Header Bar */}
        <div 
          className="flex items-center justify-between px-6 py-3 border-b"
          style={{
            backgroundColor: 'var(--card)',
            borderColor: 'var(--border)',
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        >
          {/* Left side - can be used for breadcrumbs or page title if needed */}
          <div></div>

          {/* Right side - User controls */}
          <div className="flex items-center gap-4">
            {/* Theme Selector */}
            <select
              className="px-3 py-1.5 transition-all"
              style={{
                backgroundColor: 'var(--muted)',
                color: 'var(--foreground)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-button)',
                fontSize: 'var(--text-sm)',
                cursor: 'pointer',
                outline: 'none',
              }}
              onChange={(e) => handleThemeChange(e.target.value)}
              value={currentTheme}
              title="Trocar tema"
            >
              <option value="light">☀️ Claro</option>
              <option value="dark">🌙 Escuro</option>
              <option value="auto">🔄 Auto</option>
              <option value="blue">🔵 Azul</option>
              <option value="purple">🟣 Roxo</option>
              <option value="green">🟢 Verde</option>
              <option value="orange">🟠 Laranja</option>
              <option value="rose">🌸 Rosa</option>
              <option value="cyan">🔷 Ciano</option>
              <option value="amber">🟡 Âmbar</option>
              <option value="slate">⚫ Ardósia</option>
              <option value="emerald">💚 Esmeralda</option>
            </select>

            {/* Notification Bell */}
            <NotificationBell />

            {/* User Info */}
            <div className="flex items-center gap-3 px-3 py-1.5" style={{ backgroundColor: 'var(--muted)', borderRadius: 'var(--radius-button)' }}>
              <div
                className="w-7 h-7 flex items-center justify-center overflow-hidden"
                style={{
                  backgroundColor: user?.avatar ? 'transparent' : 'var(--primary)',
                  borderRadius: '50%',
                  color: 'var(--primary-foreground)',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 'var(--font-weight-semibold)',
                  flexShrink: 0,
                }}
              >
                {user?.avatar ? (
                  <img src={user.avatar} alt={user.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  user?.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
                )}
              </div>
              <div>
                <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', lineHeight: '1.2' }}>
                  {user?.name}
                </p>
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', lineHeight: '1.2' }}>
                  {getRoleLabel(user?.role || '')}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <button
              onClick={() => setShowQuickActionsModal(true)}
              className="flex items-center justify-center p-2 transition-opacity hover:opacity-80"
              style={{
                backgroundColor: 'var(--muted)',
                color: 'var(--foreground)',
                borderRadius: 'var(--radius-button)',
                border: 'none',
                cursor: 'pointer',
              }}
              title="Ações Rápidas (Ctrl+K)"
            >
              <Zap size={18} />
            </button>

            <button
              onClick={() => setShowKeyboardModal(true)}
              className="flex items-center justify-center p-2 transition-opacity hover:opacity-80"
              style={{
                backgroundColor: 'var(--muted)',
                color: 'var(--foreground)',
                borderRadius: 'var(--radius-button)',
                border: 'none',
                cursor: 'pointer',
              }}
              title="Atalhos de Teclado (Ctrl+/)"
            >
              <Keyboard size={18} />
            </button>

            <button
              onClick={() => setShowHelpModal(true)}
              className="flex items-center justify-center p-2 transition-opacity hover:opacity-80"
              style={{
                backgroundColor: 'var(--muted)',
                color: 'var(--foreground)',
                borderRadius: 'var(--radius-button)',
                border: 'none',
                cursor: 'pointer',
              }}
              title="Ajuda (Ctrl+H)"
            >
              <HelpCircle size={18} />
            </button>
            
            <button
              onClick={() => setShowLogoutModal(true)}
              className="flex items-center justify-center p-2 transition-opacity hover:opacity-80"
              style={{
                color: 'var(--muted-foreground)',
                background: 'var(--muted)',
                border: 'none',
                cursor: 'pointer',
                borderRadius: 'var(--radius-button)',
              }}
              title="Sair"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>

        {/* Page Content */}
        <div style={{ padding: '24px' }}>
          <Outlet />
        </div>
        <Onboarding />

        {/* Toast Notification */}
        {toast && (
          <div
            style={{
              position: 'fixed',
              top: '24px',
              right: '24px',
              zIndex: 9999,
              padding: '16px 24px',
              borderRadius: 'var(--radius-card)',
              backgroundColor: toast.type === 'success' ? 'var(--success)' : toast.type === 'error' ? 'var(--destructive)' : 'var(--primary)',
              color: toast.type === 'success' ? 'var(--success-foreground)' : toast.type === 'error' ? 'var(--destructive-foreground)' : 'var(--primary-foreground)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontWeight: 'var(--font-weight-medium)',
              animation: 'slideIn 0.3s ease-out',
            }}
          >
            {toast.type === 'success' && <Check size={20} />}
            {toast.type === 'error' && <AlertTriangle size={20} />}
            {toast.type === 'info' && <Bell size={20} />}
            {toast.message}
          </div>
        )}

        {/* Logout Confirmation Modal */}
        {showLogoutModal && (
          <>
            <div
              style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 9998,
                animation: 'fadeIn 0.2s ease-out',
              }}
              onClick={() => setShowLogoutModal(false)}
            />
            <div
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'var(--card)',
                borderRadius: 'var(--radius-card)',
                padding: '24px',
                maxWidth: '400px',
                width: '90%',
                zIndex: 9999,
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                animation: 'scaleIn 0.2s ease-out',
              }}
            >
              <div className="flex items-start gap-4 mb-6">
                <div
                  style={{
                    padding: '12px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--destructive-light)',
                    color: 'var(--destructive)',
                  }}
                >
                  <LogOut size={24} />
                </div>
                <div>
                  <h3 style={{ marginBottom: '8px', fontWeight: 'var(--font-weight-semibold)' }}>
                    Confirmar Saída
                  </h3>
                  <p style={{ color: 'var(--muted-foreground)', fontSize: 'var(--text-sm)' }}>
                    Tem certeza que deseja sair do SimulaDesk? Você precisará fazer login novamente para acessar o sistema.
                  </p>
                </div>
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowLogoutModal(false)}
                  className="px-4 py-2 transition-opacity hover:opacity-80"
                  style={{
                    backgroundColor: 'var(--muted)',
                    color: 'var(--foreground)',
                    border: 'none',
                    borderRadius: 'var(--radius-button)',
                    cursor: 'pointer',
                    fontWeight: 'var(--font-weight-medium)',
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={async () => {
                    await handleLogout();
                    window.location.href = '/login';
                  }}
                  className="px-4 py-2 transition-opacity hover:opacity-90"
                  style={{
                    backgroundColor: 'var(--destructive)',
                    color: 'var(--destructive-foreground)',
                    border: 'none',
                    borderRadius: 'var(--radius-button)',
                    cursor: 'pointer',
                    fontWeight: 'var(--font-weight-semibold)',
                  }}
                >
                  Sair
                </button>
              </div>
            </div>
          </>
        )}

        {/* Help Modal */}
        {showHelpModal && (
          <>
            <div
              style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 9998,
                animation: 'fadeIn 0.2s ease-out',
              }}
              onClick={() => setShowHelpModal(false)}
            />
            <div
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'var(--card)',
                borderRadius: 'var(--radius-card)',
                padding: '24px',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '80vh',
                overflowY: 'auto',
                zIndex: 9999,
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                animation: 'scaleIn 0.2s ease-out',
              }}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div
                    style={{
                      padding: '12px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--primary-light)',
                      color: 'var(--primary)',
                    }}
                  >
                    <HelpCircle size={24} />
                  </div>
                  <h3 style={{ fontWeight: 'var(--font-weight-semibold)' }}>Central de Ajuda</h3>
                </div>
                <button
                  onClick={() => setShowHelpModal(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    color: 'var(--muted-foreground)',
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ marginBottom: '12px', fontWeight: 'var(--font-weight-semibold)' }}>
                  Bem-vindo ao SimulaDesk
                </h4>
                <p style={{ color: 'var(--muted-foreground)', fontSize: 'var(--text-sm)', marginBottom: '16px' }}>
                  Plataforma completa de treinamento e auditoria de atendimento com IA.
                </p>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ marginBottom: '12px', fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--text-sm)' }}>
                  Recursos Principais
                </h4>
                <div className="space-y-3">
                  {[
                    { icon: Mic, title: 'Treino por Voz', desc: 'Simule atendimentos com cenários realistas' },
                    { icon: FileText, title: 'Auditorias', desc: 'Análise detalhada de chamadas reais' },
                    { icon: Trophy, title: 'Gamificação', desc: 'Rankings, ligas e certificados' },
                    { icon: BarChart3, title: 'Analytics', desc: 'Indicadores e relatórios completos' },
                  ].map((item, i) => (
                    <div key={i} className="flex gap-3 p-3" style={{ backgroundColor: 'var(--muted)', borderRadius: 'var(--radius)' }}>
                      <item.icon size={20} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                      <div>
                        <p style={{ fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--text-sm)' }}>{item.title}</p>
                        <p style={{ color: 'var(--muted-foreground)', fontSize: 'var(--text-xs)' }}>{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ padding: '16px', backgroundColor: 'var(--accent)', borderRadius: 'var(--radius)', fontSize: 'var(--text-sm)' }}>
                💡 <strong>Dica:</strong> Use <kbd style={{ padding: '2px 6px', backgroundColor: 'var(--background)', borderRadius: '4px', fontSize: 'var(--text-xs)' }}>Ctrl+K</kbd> para ações rápidas
              </div>

              {/* Rever Tutorial Button */}
              <button
                onClick={() => {
                  setShowHelpModal(false);
                  setTimeout(() => startTutorial(), 200);
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 mt-4 transition-opacity hover:opacity-90"
                style={{
                  backgroundColor: 'var(--secondary)',
                  color: 'var(--secondary-foreground)',
                  borderRadius: 'var(--radius-button)',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family-inter)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-semibold)',
                }}
              >
                <HelpCircle size={16} />
                Rever Tutorial da Plataforma
              </button>
            </div>
          </>
        )}

        {/* Keyboard Shortcuts Modal */}
        {showKeyboardModal && (
          <>
            <div
              style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 9998,
                animation: 'fadeIn 0.2s ease-out',
              }}
              onClick={() => setShowKeyboardModal(false)}
            />
            <div
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'var(--card)',
                borderRadius: 'var(--radius-card)',
                padding: '24px',
                maxWidth: '500px',
                width: '90%',
                maxHeight: '80vh',
                overflowY: 'auto',
                zIndex: 9999,
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                animation: 'scaleIn 0.2s ease-out',
              }}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div
                    style={{
                      padding: '12px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--primary-light)',
                      color: 'var(--primary)',
                    }}
                  >
                    <Keyboard size={24} />
                  </div>
                  <h3 style={{ fontWeight: 'var(--font-weight-semibold)' }}>Atalhos de Teclado</h3>
                </div>
                <button
                  onClick={() => setShowKeyboardModal(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    color: 'var(--muted-foreground)',
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-3">
                {[
                  { keys: ['Ctrl', 'K'], desc: 'Abrir ações rápidas' },
                  { keys: ['Ctrl', '/'], desc: 'Ver atalhos de teclado' },
                  { keys: ['Ctrl', 'H'], desc: 'Abrir central de ajuda' },
                  { keys: ['Esc'], desc: 'Fechar modais' },
                ].map((shortcut, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between p-3"
                    style={{
                      backgroundColor: 'var(--muted)',
                      borderRadius: 'var(--radius)',
                    }}
                  >
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--foreground)' }}>
                      {shortcut.desc}
                    </span>
                    <div className="flex gap-1">
                      {shortcut.keys.map((key, j) => (
                        <kbd
                          key={j}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: 'var(--background)',
                            border: '1px solid var(--border)',
                            borderRadius: '4px',
                            fontSize: 'var(--text-xs)',
                            fontWeight: 'var(--font-weight-semibold)',
                          }}
                        >
                          {key}
                        </kbd>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Quick Actions Modal */}
        {showQuickActionsModal && (
          <>
            <div
              style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 9998,
                animation: 'fadeIn 0.2s ease-out',
              }}
              onClick={() => setShowQuickActionsModal(false)}
            />
            <div
              style={{
                position: 'fixed',
                top: '20%',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'var(--card)',
                borderRadius: 'var(--radius-card)',
                padding: '24px',
                maxWidth: '500px',
                width: '90%',
                maxHeight: '60vh',
                overflowY: 'auto',
                zIndex: 9999,
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                animation: 'scaleIn 0.2s ease-out',
              }}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div
                    style={{
                      padding: '12px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--primary-light)',
                      color: 'var(--primary)',
                    }}
                  >
                    <Zap size={24} />
                  </div>
                  <h3 style={{ fontWeight: 'var(--font-weight-semibold)' }}>Ações Rápidas</h3>
                </div>
                <button
                  onClick={() => setShowQuickActionsModal(false)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    color: 'var(--muted-foreground)',
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-2">
                {displayCategories.flatMap(cat => cat.items)
                  .filter(item => item.roles.includes(user?.role || ''))
                  .slice(0, 10)
                  .map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={i}
                        to={item.href}
                        onClick={() => setShowQuickActionsModal(false)}
                        className="flex items-center gap-3 p-3 transition-all hover:bg-opacity-80"
                        style={{
                          backgroundColor: 'var(--muted)',
                          borderRadius: 'var(--radius)',
                          textDecoration: 'none',
                          color: 'var(--foreground)',
                        }}
                      >
                        <div
                          style={{
                            padding: '8px',
                            borderRadius: '8px',
                            color: item.color,
                            backgroundColor: 'color-mix(in srgb, currentColor 12%, transparent)',
                          }}
                        >
                          <Icon size={18} />
                        </div>
                        <span style={{ fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--text-sm)' }}>
                          {item.name}
                        </span>
                      </Link>
                    );
                  })}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

